// src/components/TermPeriodsCard.jsx
// 学期の期間（教務）【2026-09-17】
//   ここで決めた月日から「今の学期」が決まり、授業を選ぶ欄にはその学期（＋通年）の授業だけが出る。
//   毎年同じ月日を既定にし、ずれる年だけ「その年だけの期間」で日付を上書きする【2026-09-18】。
//   終わりの月が始まりの月より前なら年をまたぐ（後期 9/20〜3/20）。
import React, { useEffect, useState } from 'react';
import { refreshPeriod } from './PeriodBar';
import Notice from './Notice';
import { confirmDialog } from './ConfirmDialog';

const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'application/json' });
const LAST = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** 毎年の月日を、その年度の日付に当てはめる（年度は4月始まり） */
function defaultRange(t, year) {
  const p  = (n) => String(n).padStart(2, '0');
  const last = (y, m) => new Date(y, m, 0).getDate();
  const sy = t.startMonth >= 4 ? year : year + 1;
  const ey = t.endMonth   >= 4 ? year : year + 1;
  return {
    start: `${sy}-${p(t.startMonth)}-${p(Math.min(t.startDay, last(sy, t.startMonth)))}`,
    end:   `${ey}-${p(t.endMonth)}-${p(Math.min(t.endDay,   last(ey, t.endMonth)))}`,
  };
}

/**
 * 「4/1」のように月日をそのまま打てる欄【2026-09-18】
 *   数字を1つずつ上下させる欄だと、4回触らないと期間が変えられなかった。
 *   4/1・4-1・4.1・4月1日 のどれでも受ける。
 */
function MonthDay({ value, onChange, label }) {
  return (
    <input type="text" inputMode="numeric" value={value} aria-label={label} placeholder="4/1"
           onChange={e => onChange(e.target.value)}
           style={{ width: '5.4em', padding: '0.3rem 0.4rem', textAlign: 'center' }} />
  );
}

/** 「4/1」を { m, d } にする。読めなければ null */
function parseMonthDay(text) {
  const m = String(text || '').trim().match(/^(\d{1,2})\s*[/\-.月]\s*(\d{1,2})\s*日?$/);
  if (!m) return null;
  const mo = Number(m[1]), d = Number(m[2]);
  if (mo < 1 || mo > 12 || d < 1 || d > LAST[mo - 1]) return null;
  return { m: mo, d };
}

const fmtMonthDay = (m, d) => `${m}/${d}`;

export default function TermPeriodsCard() {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [warn, setWarn] = useState([]);
  // 年度ごとの上書き【2026-09-18】
  const [year, setYear]   = useState(null);
  const [yrows, setYrows] = useState({});

  const load = async () => {
    try {
      const r = await fetch('/api/terms', { headers: auth() });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || '学期を取得できませんでした');
      setData(d);
      const m = {};
      for (const t of d.terms) m[t.id] = { s: fmtMonthDay(t.startMonth, t.startDay), e: fmtMonthDay(t.endMonth, t.endDay) };
      setRows(m);
      setYear(y => (y == null ? (d.defaults && d.defaults.academicYear) : y));
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { load(); }, []);

  // 年度か学期が変わったら、上書きの欄を入れ直す
  useEffect(() => {
    if (!data || year == null) return;
    const m = {};
    for (const t of data.terms) {
      if (t.isFullYear) continue;
      const ov = (data.termYears || []).find(
        o => Number(o.termId) === Number(t.id) && Number(o.academicYear) === Number(year)
      );
      m[t.id] = ov
        ? { start: ov.startDate, end: ov.endDate, saved: true }
        : { ...defaultRange(t, year), saved: false };
    }
    setYrows(m);
  }, [data, year]);

  const set = (id, k, v) => setRows(r => ({ ...r, [id]: { ...r[id], [k]: v } }));
  const changed = (t) => {
    const r = rows[t.id];
    if (!r) return false;
    const a = parseMonthDay(r.s), b = parseMonthDay(r.e);
    if (!a || !b) return false;
    return a.m !== t.startMonth || a.d !== t.startDay || b.m !== t.endMonth || b.d !== t.endDay;
  };
  const invalid = (r) => {
    if (!r) return '';
    const a = parseMonthDay(r.s), b = parseMonthDay(r.e);
    if (!a) return '始まりは「4/1」のように入れてください';
    if (!b) return '終わりは「9/30」のように入れてください';
    if (a.m === b.m && a.d > b.d) return '終わりが始まりより前です';
    return '';
  };

  const save = async (t) => {
    const r = rows[t.id];
    const a = parseMonthDay(r.s), b = parseMonthDay(r.e);
    if (!a || !b) return;
    setBusy(t.id); setErr(''); setMsg(''); setWarn([]);
    try {
      const res = await fetch('/api/terms/' + t.id, {
        method: 'PUT', headers: auth(),
        body: JSON.stringify({ startMonth: a.m, startDay: a.d, endMonth: b.m, endDay: b.d }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || '保存できませんでした');
      setMsg(d.message);
      setWarn(d.warnings || []);
      await refreshPeriod();
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const setY = (id, k, v) => setYrows(r => ({ ...r, [id]: { ...r[id], [k]: v } }));

  const saveYear = async (t) => {
    const r = yrows[t.id];
    if (!r || !r.start || !r.end) return;
    if (r.start > r.end) { setErr('終わりの日を始まりの日より後にしてください'); return; }
    setBusy('y' + t.id); setErr(''); setMsg(''); setWarn([]);
    try {
      const res = await fetch(`/api/terms/${t.id}/years/${year}`, {
        method: 'PUT', headers: auth(),
        body: JSON.stringify({ startDate: r.start, endDate: r.end }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || '保存できませんでした');
      setMsg(d.message);
      await refreshPeriod();
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const clearYear = async (t) => {
    if (!(await confirmDialog(`${year}年度の「${t.name}」を、毎年の既定（${defaultRange(t, year).start}〜${defaultRange(t, year).end}）に戻しますか？`))) return;
    setBusy('y' + t.id); setErr(''); setMsg('');
    try {
      const res = await fetch(`/api/terms/${t.id}/years/${year}`, { method: 'DELETE', headers: auth() });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || '戻せませんでした');
      setMsg(d.message);
      await refreshPeriod();
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const cur = data && data.current;
  return (
    <div className="card" data-term-periods>
      <div className="card-header"><h2>学期の期間</h2></div>
      <p style={{ fontSize: '0.85rem', color: '#4b5563', marginTop: 0 }}>
        授業を選ぶ欄には、今日が入る学期（学期と学期の間の休みは、次に始まる学期）と通年の授業だけが出ます。
        毎年同じ月日を使います。
      </p>
      {cur && (
        <p style={{ fontWeight: 700 }} data-current>
          今日（{data.today}）は {cur.label}{cur.range ? `（${cur.range}）` : ''}
        </p>
      )}
      <Notice kind="error" compact>{err}</Notice>
      <Notice kind="success" toast>{msg}</Notice>
      {warn.map(w => <p key={w} style={{ color: '#b45309' }}>⚠ {w}</p>)}
      {data && (
        <table className="data-table">
          <thead><tr><th>学期</th><th>期間</th><th></th></tr></thead>
          <tbody>
            {data.terms.map(t => {
              const r = rows[t.id] || {};
              const bad = invalid(r);
              return (
                <tr key={t.id} data-term={t.id}>
                  <td>{t.name}{t.isFullYear && <small style={{ color: '#6b7280' }}>（いつも表示）</small>}</td>
                  <td>
                    {t.isFullYear ? <span style={{ color: '#6b7280' }}>{fmtMonthDay(t.startMonth, t.startDay)}〜{fmtMonthDay(t.endMonth, t.endDay)}</span> : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                        <MonthDay value={r.s} label={`${t.name}の始まり（例 4/1）`} onChange={v => set(t.id, 's', v)} />
                        〜
                        <MonthDay value={r.e} label={`${t.name}の終わり（例 9/30）`} onChange={v => set(t.id, 'e', v)} />
                        {(() => {
                          const a = parseMonthDay(r.s), b = parseMonthDay(r.e);
                          return a && b && b.m < a.m ? <small style={{ color: '#6b7280' }}>（翌年）</small> : null;
                        })()}
                        {bad && <small style={{ color: '#b91c1c' }}>{bad}</small>}
                      </span>
                    )}
                  </td>
                  <td>
                    {!t.isFullYear && (
                      <button className="btn-primary" disabled={!changed(t) || !!bad || busy === t.id}
                              onClick={() => save(t)}>
                        {busy === t.id ? '保存中…' : '保存'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* ── その年だけの期間【2026-09-18】────────────────────
          前期の終わりや後期の始まりは年によって数日ずれる。
          上の月日を書き換えると過去の年度まで新しい日付に見えてしまうので、
          ずれる年だけここで日付を入れる。入れていない年は上の月日が使われる。 */}
      {data && year != null && (
        <div style={{ marginTop: '1.5rem', paddingTop: '1.1rem', borderTop: '1px solid #eef1f6' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e3a8a', marginBottom: '0.3rem' }}>
            その年だけの期間
          </h3>
          <p style={{ fontSize: '0.85rem', color: '#4b5563', margin: '0 0 0.8rem' }}>
            日付を入れた年度は、上の月日ではなくこちらが使われます。
            入れていない年度は上の月日のままなので、ずれる年だけ入れてください。
          </p>

          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.8rem' }}>
            年度
            <select value={year} onChange={e => setYear(Number(e.target.value))}>
              {(data.yearOptions || []).map(y => <option key={y} value={y}>{y}年度</option>)}
            </select>
          </label>

          <table className="data-table">
            <thead><tr><th>学期</th><th>始まり</th><th>終わり</th><th></th></tr></thead>
            <tbody>
              {data.terms.filter(t => !t.isFullYear).map(t => {
                const r  = yrows[t.id] || {};
                const badY = !r.start || !r.end || r.start > r.end;
                return (
                  <tr key={t.id} data-term-year={t.id}>
                    <td>
                      {t.name}
                      {!r.saved && <small style={{ color: '#9ca3af', marginLeft: '0.35rem' }}>既定のまま</small>}
                    </td>
                    <td>
                      <input type="date" value={r.start || ''} aria-label={`${year}年度の${t.name}の始まり`}
                             onChange={e => setY(t.id, 'start', e.target.value)} />
                    </td>
                    <td>
                      <input type="date" value={r.end || ''} aria-label={`${year}年度の${t.name}の終わり`}
                             onChange={e => setY(t.id, 'end', e.target.value)} />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-primary" disabled={busy === 'y' + t.id || badY}
                              onClick={() => saveYear(t)}>
                        {busy === 'y' + t.id ? '保存中…' : 'この年度に保存'}
                      </button>
                      {r.saved && (
                        <button className="btn-secondary" style={{ marginLeft: '0.4rem' }}
                                disabled={busy === 'y' + t.id} onClick={() => clearYear(t)}>
                          既定に戻す
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
